/**
 * Tiny copy-to-clipboard button (NIT-004).
 *
 *   <CopyButton value={JSON.stringify(payload)} label="Copy payload" />
 *
 * Shows an inline "Copied!" flash for 1.5 s on success, "Failed" on
 * error. Falls back to no-op when `navigator.clipboard` isn't
 * available (older browsers, jsdom in unit tests).
 */
import { useState } from 'react';

interface Props {
  readonly value: string;
  /** Short text shown when idle. Defaults to "Copy". */
  readonly label?: string;
  /** Pass through extra Tailwind classes (caller controls sizing). */
  readonly className?: string;
  /** Optional data-testid suffix for granular e2e selectors. */
  readonly testId?: string;
}

export function CopyButton({ value, label = 'Copy', className, testId }: Props): JSX.Element {
  const [state, setState] = useState<'idle' | 'copied' | 'error'>('idle');

  const handle = (): void => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) {
      setState('error');
      return;
    }
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setState('copied');
        setTimeout(() => setState('idle'), 1_500);
      })
      .catch(() => {
        setState('error');
        setTimeout(() => setState('idle'), 1_500);
      });
  };

  return (
    <button
      type="button"
      onClick={handle}
      className={`rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-700 focus:outline-none focus:ring-1 focus:ring-primary-base ${className ?? ''}`}
      data-testid={testId !== undefined ? `copy-${testId}` : 'copy'}
      aria-label={state === 'copied' ? 'Copied' : label}
    >
      {state === 'copied' ? '✓ Copied' : state === 'error' ? '✗ Failed' : label}
    </button>
  );
}
